import { ref, defineComponent, provide, reactive, onMounted, onUnmounted, nextTick, watch, computed, } from 'vue';
import _ from 'lodash';
import useConfigInject from '../../_util/hooks/useConfigInject';
import { pieChartContextKey } from './constants';
import { getColors } from '../../_util/chartsColors';
import classNames from '../../_util/classNames';
import Legend from './legend';
import Spin from '../../spin';
import * as echarts from 'echarts/core';
import { PieChart as EchartPieChart } from 'echarts/charts';
import { TitleComponent, TooltipComponent } from 'echarts/components';
import { CanvasRenderer, SVGRenderer } from 'echarts/renderers';
import { pieLegendFormatter, pieTooltipFormatter } from './computed';
import { inThanMinOrMax } from 'wetrade-tools';
import BigNumber from 'bignumber.js';
export const QuotationColorMap = {
    redUp: 0,
    greenUp: 1,
};
// 注册必须的组件
echarts.use([EchartPieChart, TitleComponent, TooltipComponent, CanvasRenderer, SVGRenderer]);
export const PieChartProps = () => ({
    quotationColor: {
        type: Number,
        default: 0,
    },
    loading: {
        type: Boolean,
        default: false,
    },
    // 饼图颜色
    colors: {
        type: Array,
        default: () => [],
    },
    width: {
        type: [Number, String],
        default: '200px',
    },
    // 画布高度
    height: {
        type: [Number, String],
        default: '200px',
    },
    chartData: {
        type: Array,
        default: () => [],
    },
    // 顶部距离
    top: {
        type: [Number, String],
        default: '50%',
    },
    // 左侧距离
    left: {
        type: [Number, String],
        default: '50%',
    },
    // 图例
    legend: {
        type: Array,
        default: () => [],
    },
    // 内圈外圈的值 number[]
    radius: {
        type: Array,
        default: () => [45, 90],
    },
    // 内外边框距离 number[]
    withinOutsideDistance: {
        type: Array,
        default: () => [6, 10],
    },
    // 是否有hover浮层
    isToolltip: {
        type: Boolean,
        default: true,
    },
    // 是否显示占比
    accountedFor: {
        type: Boolean,
        default: false,
    },
    // 是否显示数值
    originalValue: {
        type: Boolean,
        default: false,
    },
    // 是否显示千位符
    showThousandSign: {
        type: Boolean,
        default: false,
    },
    // 保留小数位n位
    digits: {
        type: Number,
        default: 2,
    },
    // 文字布局方式
    direction: {
        type: String,
        default: 'horizontal',
    },
    // 起始角度
    theAngle: {
        type: Number,
        default: 90,
    },
    // 最小角度
    minAngle: {
        type: Number,
        default: 0,
    },
    // 是否显示图例
    isLegend: {
        type: Boolean,
        default: false,
    },
    // 图例位置
    location: {
        type: Object,
        default: () => ({
            top: 'center',
            right: 20,
        }),
    },
    // 图例是否可以点击
    selectedMode: {
        type: Boolean,
        default: false,
    },
    animation: {
        type: Boolean,
        default: true,
    },
    // 图表动画时长
    animationTime: {
        type: Number,
        default: 1000,
    },
    // 汇总中间值参数
    centerTitle: {
        type: String,
        default: '',
    },
    // 汇总中间值参数总值
    totalSum: {
        type: [Number, String],
        default: '',
    },
    // 内容是否显示在中间
    isCenter: {
        type: Boolean,
        default: false,
    },
    // 是否显示tootip
    isTooltip: {
        type: Boolean,
        default: true,
    },
    confine: {
        type: Boolean,
        default: false,
    },
    // 是否显示视觉引导线
    isGuideLine: {
        type: Boolean,
        default: false,
    },
    // 是否有链接色
    isToLink: {
        type: Boolean,
        default: true,
    },
    // 是否自定义显示tootip
    customTooltip: {
        type: Boolean,
        default: false,
    },
    // 是否固定tooltip位置，目前固定在右侧
    tooltipFix: {
        type: Boolean,
        default: false,
    },
    tooltipPosition: {
        type: Function,
        default: () => {
            return null;
        },
    },
    // 是否过滤内容, 默认true
    filterZero: {
        type: Boolean,
        default: true,
    },
    // 图例的处理
    pieLegendFormatter: {
        default: () => pieLegendFormatter,
    },
    // tooltip处理
    pieTooltipFormatter: {
        default: () => pieTooltipFormatter,
    },
    // label是否要删除[]及内容，适用于tooltip；selectedModel=true时，需要增加唯一标识确定不同的label，显示时需删除
    isLabelRemoveId: {
        type: Boolean,
        default: false,
    },
    // 图例选中状态表，动态配置图例显示项，selectedMode=true时生效
    selectedModel: {
        type: Object,
        default: () => {
            return {};
        },
    },
    // 在文字后面增加动态样式及文案 举例：线下银行增加“转”字用到
    toolTipPatchFunc: {
        default: () => null,
    },
    // label字段名，自定义样式时使用
    labelName: {
        type: String,
        default: '',
    },
    // 显示自定义图例
    showCustomLegend: {
        type: Boolean,
        default: true,
    },
    // 图例 - 数据
    legendList: {
        type: Array,
        default: () => [],
    },
    legendWidth: {
        type: String,
        default: '',
    },
    // 图例的值是数值型还是百分比
    isLegendRatio: {
        type: Boolean,
        default: false,
    },
    // 数值是否需要换算，单位有万，亿，万亿，默认不换算
    isConversion: {
        type: [Boolean, Array],
        default: false,
    },
    unit: {
        type: [String, Array],
        default: '',
    },
    // 0和null显示为0还是--
    valueIsNumber: {
        type: Boolean,
        default: false,
    },
    labelLineLength: {
        type: Array,
        default: () => [20, 20],
    },
    customGuide: {
        type: Function,
        default: () => {
            return null;
        },
    },
    legendMarginLeft: {
        type: String,
        default: '',
    },
    emphasisScale: {
        type: Boolean,
        default: true,
    },
    emphasisScaleSize: {
        type: Number,
        default: 5,
    },
    labelRich: {
        type: Object,
        default: null,
    },
    renderer: {
        type: String,
        default: 'svg',
    },
    isValueComputed: {
        type: Boolean,
        default: false,
    },
    emphasisFocus: {
        type: Boolean,
        default: false,
    },
});
const PieChart = defineComponent({
    name: 'WdPieChart',
    Legend,
    props: PieChartProps(),
    slots: ['mark'],
    setup(props, { emit, expose, slots }) {
        const { prefixCls, configProvider } = useConfigInject('pie-chart', props);
        const wrapRef = ref();
        const mainRef = ref();
        const mainChart = ref();
        let myChart = null;
        // 基本配置
        const init = () => {
            if (myChart) {
                myChart.clear();
                myChart.dispose();
                myChart = null;
            }
            if (!mainChart.value)
                return;
            myChart = echarts.init(mainChart.value, null, {
                renderer: props.renderer,
            });
            const colors = getColors(configProvider?.mode, props.quotationColor); // 赋值给常量，避免多处访问实例this
            const option = {
                color: getColorType.value,
                legend: {
                    show: props.isLegend,
                    icon: 'circle',
                    itemWidth: 8,
                    itemHeight: 8,
                    orient: props.direction,
                    itemGap: 16,
                    selectedMode: props.selectedMode,
                    ...props.location,
                    itemStyle: {
                        borderWidth: 0,
                    },
                    selected: props.selectedModel,
                    textStyle: {
                        fontSize: 12,
                        color: colors.TEXT,
                        padding: [2, 0, 0, 8],
                        //新版本支持 overflow: 'truncate',
                        rich: {
                            a: {
                                align: 'left',
                                width: 82,
                                padding: [0, 8, 0, 0],
                            },
                            b: {
                                align: 'right',
                                width: 54,
                                padding: [0, 8, 0, 0],
                            },
                            c: {
                                align: 'right',
                                width: 54,
                            },
                        },
                    },
                    formatter: name => props.pieLegendFormatter({
                        name,
                        total: getTotalValue.value,
                        list: props.chartData,
                        props,
                    }),
                },
                tooltip: {
                    show: !getTotalValue.value ? false : props.isTooltip,
                    padding: 12,
                    transitionDuration: 0,
                    extraCssText: colors.PIE_TOOLTIP_SHADOW,
                    backgroundColor: colors.TOOLTIP_BG,
                    borderColor: colors.TOOLTIP_BG,
                    confine: props.confine,
                    position: (pos) => {
                        if (props.tooltipPosition(pos))
                            return props.tooltipPosition(pos);
                        if (!props.tooltipFix)
                            return null;
                        const obj = { top: pos[1] + 20, left: pos[0] + 20 };
                        return obj;
                    },
                    textStyle: {
                        color: colors.TEXT,
                        fontSize: 12,
                        fontFamily: 'custom-fonts',
                    },
                    axisPointer: {
                        axis: 'radius',
                        animation: false,
                    },
                    formatter: (v) => props.pieTooltipFormatter({
                        v,
                        total: getTotalValue.value,
                        list: props.chartData,
                        props,
                    }),
                },
                series: handleSeries(),
            };
            // 使用刚指定的配置项和数据显示图表。
            myChart.setOption(option);
        };
        // 截取图例多余文字
        const showName = (value) => {
            const findIndexs = value?.indexOf('[');
            if (findIndexs > -1) {
                const seriesName = value.substring(0, findIndexs);
                return seriesName;
            }
            return value;
        };
        const handleSeries = () => {
            const colors = getColors(configProvider?.mode, props.quotationColor);
            const series = [
                {
                    type: 'pie',
                    radius: props.radius,
                    center: [props.left, props.top],
                    avoidLabelOverlap: true,
                    itemStyle: {
                        borderColor: colors.PIE_BG,
                        borderWidth: 2,
                    },
                    startAngle: props.theAngle,
                    minAngle: props.minAngle,
                    animation: props.animation,
                    animationEasingUpdate: 'quadraticOut',
                    animationDuration: props.animationTime,
                    emphasis: {
                        scale: props.emphasisScale,
                        scaleSize: props.emphasisScaleSize,
                        itemStyle: {
                            opacity: 1,
                        },
                        focus: props.emphasisFocus ? 'self' : 'none',
                    },
                    label: {
                        show: props.isGuideLine,
                        formatter: (params) => {
                            const ratio = props.isValueComputed
                                ? params.value
                                : new BigNumber(params.value).div(getTotalValue.value).times(100).toNumber();
                            return props.customGuide()
                                ? props.customGuide(params, getTotalValue.value)
                                : '{a|' +
                                    (props.isLabelRemoveId
                                        ? showName(params.data[props.labelName])
                                        : params.data[props.labelName]) +
                                    '}' +
                                    '\n' +
                                    '{b|' +
                                    (params?.data?.percent
                                        ? params?.data?.percent
                                        : inThanMinOrMax({
                                            value: ratio,
                                            decimal: props.digits,
                                            isPercent: true,
                                        })) +
                                    '}';
                        },
                        rich: props.labelRich || {
                            a: {
                                color: colors.TEXT,
                                fontSize: 12,
                                lineHeight: 20,
                            },
                            b: {
                                fontSize: 12,
                                lineHeight: 20,
                                color: colors.TEXT,
                            },
                            c: {
                                fontSize: 12,
                                lineHeight: 20,
                                color: colors.TEXT,
                            },
                        },
                    },
                    labelLine: {
                        length: props.labelLineLength[0],
                        length2: props.labelLineLength[1],
                    },
                    data: getDateClear.value,
                    z: !getTotalValue.value ? 0 : 2,
                },
                // 灰色背景环
                {
                    type: 'pie',
                    radius: [
                        props.radius[0] - props.withinOutsideDistance[0],
                        props.radius[1] + props.withinOutsideDistance[1],
                    ],
                    center: [props.left, props.top],
                    data: [],
                    itemStyle: {
                        color: colors.PIE_BG,
                    },
                    emphasis: {
                        // 高亮状态的扇区和标签样式。
                        itemStyle: {
                            color: colors.PIE_BG,
                        },
                    },
                    emptyCircleStyle: {
                        color: colors.PIE_BG,
                    },
                    label: {
                        show: false,
                    },
                    labelLine: {
                        show: false,
                    },
                    tooltip: {
                        show: false,
                    },
                    animation: false,
                    cursor: 'auto',
                    z: 1,
                },
            ];
            return series;
        };
        // 获取饼图颜色
        const getColorType = computed(() => {
            if (props.colors.length)
                return props.colors;
            return getColors(configProvider?.mode, props.quotationColor).COMMONCOLOR;
        });
        //计算数据value是否为0
        const getTotalValue = computed(() => {
            let sum = 0;
            let isNil = true;
            if (!props.chartData?.length)
                return sum;
            for (let i = 0; i < props.chartData.length; i++) {
                // 如果可以置灰，计算时需要把置灰的数据屏蔽掉（取原始值来做判断，名称可能会更改）
                const flag = Object.keys(props.selectedModel || {}).length
                    ? props.selectedModel[props.chartData[i]?.crrentName
                        ? props.chartData[i]?.crrentName
                        : props.chartData[i]?.name]
                    : true;
                if (props.chartData[i].value > 0 && flag) {
                    sum += props.chartData[i].value;
                }
                if (props.chartData[i].value !== null)
                    isNil = false;
            }
            // 如果值都为null，则返回null
            return isNil ? null : sum;
        });
        // 数据过滤组装
        const getDateClear = computed(() => {
            if (!props.chartData?.length)
                return [];
            const newArray = _.cloneDeep(props.chartData);
            newArray.forEach((i) => {
                if (i.value === 0 && props.filterZero)
                    i.value = '';
            });
            return newArray;
        });
        // 更新主题
        const updateColor = () => {
            if (!myChart)
                return;
            const colors = getColors(configProvider?.mode, props.quotationColor); // 赋值给常量，避免多处访问实例this
            const option = {
                color: getColorType.value,
                legend: {
                    textStyle: {
                        color: colors.TEXT,
                    },
                },
                tooltip: {
                    backgroundColor: colors.TOOLTIP_BG,
                    borderColor: colors.TOOLTIP_BG,
                    textStyle: {
                        color: colors.TEXT,
                    },
                    extraCssText: `box-shadow: 0 0 3px ${colors.PIE_TOOLTIP_SHADOW};`,
                },
                series: [
                    {
                        itemStyle: {
                            borderColor: colors.PIE_BG,
                        },
                        label: {
                            normal: {
                                rich: props.labelRich || {
                                    a: {
                                        color: colors.TEXT,
                                    },
                                    b: {
                                        color: colors.TEXT,
                                    },
                                    c: {
                                        color: colors.TEXT,
                                    },
                                },
                            },
                        },
                    },
                    {
                        itemStyle: {
                            color: colors.PIE_BG,
                        },
                        emphasis: {
                            itemStyle: {
                                color: colors.PIE_BG,
                            },
                        },
                        emptyCircleStyle: {
                            color: colors.PIE_BG,
                        },
                    },
                ],
            };
            myChart?.setOption(option);
        };
        const updateData = () => {
            if (!myChart)
                return;
            const option = {
                title: {
                    show: !getTotalValue.value ? true : false,
                },
                color: getColorType.value,
                tooltip: {
                    show: !getTotalValue.value ? false : props.isTooltip,
                },
                series: [
                    {
                        data: getDateClear.value,
                        z: !getTotalValue.value ? 0 : 2,
                    },
                ],
            };
            nextTick(() => myChart.setOption(option));
        };
        // 更新可显示项
        const updateSelected = () => {
            if (!myChart)
                return;
            const option = {
                animation: false,
                legend: {
                    show: false,
                    selected: props.selectedModel,
                },
                series: handleSeries(),
            };
            myChart.setOption(option);
        };
        const toLink = () => {
            emit('linkUrl');
        };
        const handlerResize = () => {
            myChart?.resize();
        };
        // 点击图例
        const onLegendChange = (t) => {
            emit('legendChange', t);
        };
        watch(() => [configProvider?.mode, props.quotationColor], () => {
            nextTick(() => {
                updateColor();
            });
        });
        provide(pieChartContextKey, reactive({
            pieChartElement: mainRef,
            wrapElement: wrapRef,
        }));
        onMounted(() => {
            init();
        });
        onUnmounted(() => {
            if (myChart) {
                myChart.clear();
                myChart.dispose();
                myChart = null;
            }
            wrapRef.value = null;
            mainRef.value = null;
            mainChart.value = null;
        });
        expose({
            /** @description init chart */
            init,
            /** @description update data */
            updateData,
            /** @description update color */
            updateColor,
            /** @description update selectedModel*/
            updateSelected,
            /** @description onRisize */
            handlerResize,
        });
        return () => {
            let LEGENDDOM, CENTERDOM;
            if (props.showCustomLegend && (props.legendList?.length || props.legend?.length)) {
                LEGENDDOM = (<Legend legendMarginLeft={props.legendMarginLeft} height={props.height} legendWidth={props.legendWidth} legendList={props.legendList?.length ? props.legendList : props.legend} selectedMode={props.selectedMode} selectedModel={props.selectedModel} isLabelRemoveId={props.isLabelRemoveId} isValueComputed={props.isValueComputed} colors={props.colors} isLegendRatio={props.isLegendRatio} digits={props.digits} showThousandSign={props.showThousandSign} totalValue={getTotalValue.value} isConversion={props.isConversion} unit={props.unit} valueIsNumber={props.valueIsNumber} onLegendChange={onLegendChange} v-slots={slots}/>);
            }
            // 是否有居中的title
            if (props.isCenter) {
                // 居中文字盒子
                const centerBoxStyle = {
                    top: typeof props.top === 'string' ? props.top : props.top + 'px',
                    left: typeof props.left === 'string' ? props.left : props.left + 'px',
                };
                // 居中文字标题
                const titleClass = classNames({
                    [prefixCls.value + '__center-content__title']: true,
                    [prefixCls.value + '__center-content__hasNum']: props.totalSum || props.totalSum === 0,
                });
                // 居中文字标题
                const textClass = classNames({
                    [prefixCls.value + '__center-content__text']: true,
                    [prefixCls.value + '__center-content__links']: props.isToLink,
                });
                CENTERDOM = (<div class={`${prefixCls.value}__center-content`} style={centerBoxStyle}>
            <h3 class={titleClass}>{props.centerTitle}</h3>
            <span class={textClass} onClick={() => toLink()}>
              {props.totalSum}
            </span>
          </div>);
            }
            const bodyStyle = {
                width: typeof props.width === 'string' ? props.width : props.width + 'px',
                height: typeof props.height === 'string' ? props.height : props.height + 'px',
            };
            // 居中文字标题
            const boxClass = classNames({
                [prefixCls.value]: true,
                [prefixCls.value + '__' + props.direction]: true,
            });
            const mainStyle = {
                height: typeof props.height === 'string' ? props.height : props.height + 'px',
            };
            return (<Spin spinning={props.loading}>
          <div class={boxClass}>
            <div ref={mainRef} class={`${prefixCls.value}__chart`} style={mainStyle}>
              {CENTERDOM}
              <div ref={mainChart} class={`${prefixCls.value}__body`} style={bodyStyle}></div>
            </div>
            {LEGENDDOM}
          </div>
        </Spin>);
        };
    },
});
export default PieChart;
